const DEFAULT_TTL_MS = 5 * 60 * 1000;
const STORAGE_PREFIX = "longfans.apiCache.";

interface CacheEntry<T> {
  cachedAt: number;
  data: T;
}

const memoryEntries = new Map<string, CacheEntry<unknown>>();
const inflightRequests = new Map<string, Promise<unknown>>();

function sessionStorageKey(key: string): string {
  return `${STORAGE_PREFIX}${key}`;
}

function readSessionEntry<T>(key: string, ttlMs: number): CacheEntry<T> | null {
  if (typeof sessionStorage === "undefined") {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(sessionStorageKey(key));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (!parsed || typeof parsed.cachedAt !== "number" || parsed.data === undefined) {
      return null;
    }
    if (Date.now() - parsed.cachedAt > ttlMs) {
      sessionStorage.removeItem(sessionStorageKey(key));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSessionEntry<T>(key: string, entry: CacheEntry<T>): void {
  memoryEntries.set(key, entry as CacheEntry<unknown>);
  if (typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.setItem(sessionStorageKey(key), JSON.stringify(entry));
  } catch {
    /* quota exceeded 等场景忽略 */
  }
}

function readCachedData<T>(key: string, ttlMs: number): T | null {
  const mem = memoryEntries.get(key) as CacheEntry<T> | undefined;
  if (mem && Date.now() - mem.cachedAt <= ttlMs) {
    return mem.data;
  }
  const fromSession = readSessionEntry<T>(key, ttlMs);
  if (!fromSession) {
    return null;
  }
  memoryEntries.set(key, fromSession as CacheEntry<unknown>);
  return fromSession.data;
}

export function clearApiSessionCache(key?: string): void {
  if (key) {
    memoryEntries.delete(key);
    inflightRequests.delete(key);
    if (typeof sessionStorage !== "undefined") {
      try {
        sessionStorage.removeItem(sessionStorageKey(key));
      } catch {
        /* ignore */
      }
    }
    return;
  }
  memoryEntries.clear();
  inflightRequests.clear();
  if (typeof sessionStorage === "undefined") {
    return;
  }
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(k);
      }
    }
    keysToRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

/** API 层 sessionStorage 缓存，默认 TTL 5 分钟；同 key 并发请求合并。 */
export async function getOrFetchApiCache<T>(
  key: string,
  fetcher: () => Promise<T>,
  options?: { ttlMs?: number }
): Promise<T> {
  const ttlMs = options?.ttlMs ?? DEFAULT_TTL_MS;
  const cached = readCachedData<T>(key, ttlMs);
  if (cached !== null) {
    return cached;
  }

  const inflight = inflightRequests.get(key);
  if (inflight) {
    return inflight as Promise<T>;
  }

  const promise = (async () => {
    try {
      const data = await fetcher();
      writeSessionEntry(key, { cachedAt: Date.now(), data });
      return data;
    } finally {
      inflightRequests.delete(key);
    }
  })();

  inflightRequests.set(key, promise);
  return promise;
}
